<script>
  import { onMount } from "svelte";
  import Icon from "@iconify/svelte";
  import serverIcon from "@iconify/icons-mdi/cog";
  import tagIcon from "@iconify/icons-mdi/tag-outline";
  import keyIcon from "@iconify/icons-mdi/key";
  import saveIcon from "@iconify/icons-mdi/content-save";
  import checkIcon from "@iconify/icons-mdi/check";
  import { t } from "./lib/i18n.js";

  export let apiBase = "";
  export let apiKey = "";
  export let setup = null;
  export let setupErr = "";
  export let locale = "en";
  export let onApiError = () => {};

  // Settings stored in Redis (API); switchable in real time
  let webhookSecret = "";
  let signatureHeader = "X-Hub-Signature-256";
  let rejectAll = false;
  let rejectReason = "412";
  let settingsSaveStatus = ""; // '' | 'saving' | 'saved' | 'error'
  const signatureHeaderId = "signature-header";
  const secretValueId = "secret-value";
  const rejectReasonId = "reject-reason";

  function rejectionOptions(locale) {
    return [
      { value: "random", label: t(locale, "reject_random") },
      { value: "cors", label: `403 - ${t(locale, "reject_cors")}` },
      { value: "400", label: `400 - ${t(locale, "http_400")}` },
      { value: "401", label: `401 - ${t(locale, "http_401")}` },
      { value: "403", label: `403 - ${t(locale, "http_403")}` },
      { value: "404", label: `404 - ${t(locale, "http_404")}` },
      { value: "405", label: `405 - ${t(locale, "http_405")}` },
      { value: "408", label: `408 - ${t(locale, "http_408")}` },
      { value: "409", label: `409 - ${t(locale, "http_409")}` },
      { value: "410", label: `410 - ${t(locale, "http_410")}` },
      { value: "412", label: `412 - ${t(locale, "http_412")}` },
      { value: "422", label: `422 - ${t(locale, "http_422")}` },
      { value: "429", label: `429 - ${t(locale, "http_429")}` },
      { value: "500", label: `500 - ${t(locale, "http_500")}` },
      { value: "502", label: `502 - ${t(locale, "http_502")}` },
      { value: "503", label: `503 - ${t(locale, "http_503")}` },
      { value: "504", label: `504 - ${t(locale, "http_504")}` }
    ];
  }

  async function loadSettings() {
    try {
      const res = await fetch(`${apiBase}/settings`, {
        headers: { Accept: "application/json", ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}) }
      });
      if (res.ok) {
        const data = await res.json();
        signatureHeader = data.signature_header || "X-Hub-Signature-256";
        rejectAll = data.reject_all === true;
        const r = data.reject_reason || "412";
        rejectReason = REJECTION_VALUES.includes(String(r)) ? String(r) : "412";
      }
    } catch {}
  }

  async function saveSettings() {
    settingsSaveStatus = "saving";
    try {
      const res = await fetch(`${apiBase}/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {})
        },
        body: JSON.stringify({
          secret: webhookSecret,
          signature_header: signatureHeader,
          reject_all: rejectAll,
          reject_reason: rejectReason
        })
      });
      if (res.status === 401) {
        onApiError(t(locale, "auth_required"));
        settingsSaveStatus = "error";
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        onApiError(err.error || `HTTP ${res.status}`);
        settingsSaveStatus = "error";
        return;
      }
      settingsSaveStatus = "saved";
      setTimeout(() => (settingsSaveStatus = ""), 2000);
    } catch (e) {
      onApiError(e.message);
      settingsSaveStatus = "error";
    }
  }

  onMount(() => {
    if (apiBase) loadSettings();
  });

  function item(label, value, hint = "") {
    return { label, value, hint };
  }

  function section(title, items) {
    return { title, items };
  }

  function pretty(v) {
    const s = (v ?? "").toString().trim();
    return s ? s : "—";
  }

  function maskSecret(v) {
    const s = (v ?? "").toString().trim();
    if (!s) return "—";
    if (s.length <= 2) return "****";
    return s[0] + "****" + s[s.length - 1];
  }

  function isSecret(name) {
    const n = (name ?? "").toUpperCase();
    return n.includes("PASSWORD") || n.includes("SECRET") || n.includes("_KEY");
  }

  function groupEnvVars(envVars = []) {
    const grouped = new Map();
    for (const env of envVars) {
      const key = (env?.name || "General").toString();
      let group;
      if (key === "DEFAULT_WEBHOOKS" || key.startsWith("WEBHOOK_")) {
        group = "Webhooks";
      } else {
        group = key.includes("_") ? key.split("_")[0] : "General";
      }
      if (!grouped.has(group)) grouped.set(group, []);
      grouped.get(group).push(env);
    }
    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, items]) => ({ group, items }));
  }

  function orderedSections(envVars) {
    if (!setup) return [];
    const statusSection = section(t(locale, "status"), [
      item(t(locale, "connection"), setup.connected ? t(locale, "connected") : t(locale, "disconnected")),
      item(t(locale, "auth"), setup.auth_enabled ? t(locale, "enabled") : t(locale, "disabled"))
    ]);
    const groups = groupEnvVars(envVars);
    const envSections = groups
      .filter((g) => g.group !== "Webhooks")
      .map((g) =>
        section(
          g.group,
          g.items.map((env) => {
            const raw = pretty(env.value || env.default);
            const display = isSecret(env.name) ? maskSecret(env.value || env.default) : raw;
            return item(
              env.name,
              display,
              env.value ? "" : env.default ? t(locale, "default") : ""
            );
          })
        )
      );
    return [statusSection, ...envSections];
  }

  $: sections = setup ? orderedSections(setup.env_vars || []) : [];

  const mono =
    "font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis";
  const sectionCard = "ui-card-inner p-4 sm:p-5";
  const cell = "rounded border border-white/10 bg-white/[0.03] p-3 min-w-0";
  const klabel = "text-xs text-zinc-400 flex flex-wrap items-center gap-2";
  const khint =
    "text-[11px] text-zinc-500 inline-flex items-center gap-1 whitespace-nowrap";
  $: REJECTION_OPTIONS = rejectionOptions(locale);
  $: REJECTION_VALUES = REJECTION_OPTIONS.map((o) => o.value);
</script>

<main class="w-full p-4 sm:p-6">
  <section class="ui-card p-5 sm:p-6">
    <!-- Header -->
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center rounded border border-violet-400/60 bg-violet-400/20 text-white"
        >
          <Icon icon={serverIcon} width="18" height="18" />
        </span>

        <div>
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">
            {t(locale, "settings_title")}
          </h1>
          <p class="mt-0.5 text-sm text-zinc-400 hidden sm:block">
            {t(locale, "settings_subtitle")}
          </p>
        </div>
      </div>
    </div>

    <!-- Error -->
    {#if setupErr}
      <div
        class="mt-4 rounded border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100 flex items-center gap-2"
      >
        <span class="break-words">{setupErr}</span>
      </div>
    {/if}

    {#if !setup}
      <div
        class="mt-6 ui-card-inner p-4 text-sm text-zinc-400"
      >
        {t(locale, "loading")}
      </div>
    {:else}
      <!-- Signature verification (stored in Redis; sig type used to calculate, then secret) -->
      <div class={sectionCard + " mt-6"}>
        <div class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3">
          <div class="flex items-center gap-2">
            <Icon icon={keyIcon} width="16" height="16" class="text-flytrap-neon" />
            <div class="text-sm font-semibold tracking-wide text-white">{t(locale, "settings_signature_title")}</div>
          </div>
        </div>
        <p class="mt-2 text-xs text-zinc-500">
          {t(locale, "settings_signature_hint")}
        </p>
        <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:items-end">
          <div class="min-w-0">
            <label class={klabel} for={signatureHeaderId}>{t(locale, "settings_signature_header")}</label>
            <select
              id={signatureHeaderId}
              bind:value={signatureHeader}
              class="input-focus mt-2 w-full rounded border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-xs text-zinc-200 focus:outline-none"
            >
              <option value="X-Hub-Signature-256">X-Hub-Signature-256 (GitHub)</option>
              <option value="X-Hub-Signature">X-Hub-Signature (GitHub SHA1)</option>
              <option value="X-Webhook-Signature">X-Webhook-Signature</option>
              <option value="X-Signature">X-Signature</option>
              <option value="Stripe-Signature">Stripe-Signature</option>
            </select>
          </div>
          <div class="min-w-0">
            <label class={klabel} for={secretValueId}>{t(locale, "settings_secret_value")}</label>
            <input
              id={secretValueId}
              type="text"
              bind:value={webhookSecret}
              placeholder={t(locale, "optional")}
              class="input-focus mt-2 w-full rounded border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-xs text-zinc-200 placeholder-zinc-500 focus:outline-none"
            />
          </div>
          <div class="sm:col-span-2 flex justify-end">
            <button
              type="button"
              class="inline-flex items-center justify-center gap-2 rounded-lg border border-violet-400/60 bg-violet-400/20 px-4 py-2 text-sky-200 hover:bg-sky-400/30 transition-colors disabled:opacity-50"
              disabled={settingsSaveStatus === "saving"}
              onclick={saveSettings}
              title={t(locale, "save")}
            >
              {#if settingsSaveStatus === "saved"}
                <Icon icon={checkIcon} width="18" height="18" />
              {:else}
                <Icon icon={saveIcon} width="18" height="18" />
              {/if}
            </button>
          </div>
        </div>
      </div>

      <!-- Rejection (always fail) – stored in Redis, toggled in real time -->
      <div class={sectionCard + " mt-6"}>
        <div class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3">
          <div class="flex items-center gap-2">
            <Icon icon={serverIcon} width="16" height="16" class="text-flytrap-neon" />
            <div class="text-sm font-semibold tracking-wide text-white">{t(locale, "settings_rejection_title")}</div>
          </div>
        </div>
        <p class="mt-2 text-xs text-zinc-500">
          {t(locale, "settings_rejection_hint")}
        </p>
        <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:items-end">
          <div class="min-w-0 flex items-center gap-3">
            <label class="relative inline-flex h-6 w-10 flex-shrink-0 cursor-pointer">
              <input type="checkbox" bind:checked={rejectAll} class="peer sr-only" />
              <span class="peer h-6 w-10 rounded-full border border-white/10 bg-white/5 transition-colors peer-checked:bg-sky-400/30 peer-checked:border-sky-400/50 peer-focus:ring-2 peer-focus:ring-sky-400/30"></span>
              <span class="absolute left-1 top-1 z-10 h-4 w-4 rounded-full bg-zinc-400 transition-all peer-checked:left-5 peer-checked:bg-sky-300"></span>
            </label>
            <span class="text-sm text-zinc-200">{t(locale, "settings_reject_all")}</span>
          </div>
          <div class="min-w-0 flex items-end gap-2">
            <div class="flex-1 min-w-0">
              <label class={klabel} for={rejectReasonId}>{t(locale, "settings_status_code")}</label>
              <select
                id={rejectReasonId}
                bind:value={rejectReason}
                class="input-focus mt-2 w-full rounded border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-xs text-zinc-200 focus:outline-none"
              >
                {#each REJECTION_OPTIONS as opt}
                  <option value={opt.value}>{opt.label}</option>
                {/each}
              </select>
            </div>
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-lg border border-violet-400/60 bg-violet-400/20 p-2 text-sky-200 hover:bg-sky-400/30 transition-colors disabled:opacity-50 flex-shrink-0"
              disabled={settingsSaveStatus === "saving"}
              onclick={saveSettings}
              title={t(locale, "save")}
            >
              {#if settingsSaveStatus === "saved"}
                <Icon icon={checkIcon} width="18" height="18" />
              {:else}
                <Icon icon={saveIcon} width="18" height="18" />
              {/if}
            </button>
          </div>
        </div>
      </div>

      <!-- Env / status sections -->
      <div class="mt-6 grid grid-cols-1 gap-4">
        {#each sections as s (s.title)}
          <div class={sectionCard}>
            <div
              class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3"
            >
              <div class="flex items-center gap-2">
                <Icon icon={tagIcon} width="16" height="16" class="text-flytrap-neon" />
                <div class="text-sm font-semibold tracking-wide text-white">
                  {s.title}
                </div>
              </div>

              <div class="text-xs text-zinc-500">{t(locale, "items_count", { count: s.items.length })}</div>
            </div>

            <div
              class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {#each s.items as it (it.label)}
                <div class={cell}>
                  <div class={klabel}>
                    <span>{it.label}</span>
                    {#if it.hint}
                      <span class={khint}>• {it.hint}</span>
                    {/if}
                  </div>

                  <div class={`${mono} mt-2`} title={it.value}>
                    {it.value}
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </section>
</main>
